import React, { useState } from 'react'

import { Box, Button, Tag, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
// TODO: consider if this should be public or bookkeepers belongs to payroll_bundle
import param from 'jquery-param'

import { observer } from 'mobx-react'

import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import type { IncidenceQueryData } from 'modules/bookkeepers/hooks/useGetIncidences'
import { statusTranslation } from 'modules/bookkeepers/lib/dynamicTranslations'
import { getBookkeeperStatusTagColor, reopenedTagColor } from 'modules/bookkeepers/lib/tagColor'
import { useInhousePayroll } from 'modules/payroll_bundle/hooks/useInhousePayroll'
import { INCIDENCE_STATUSES } from 'modules/payroll_bundle/lib/Incidences/Status'
import { INCIDENCE_TYPES } from 'modules/payroll_bundle/lib/Incidences/Types'
import { downloadFile } from 'modules/payroll_exporter/lib/downloadFile'
import { ExportFormat } from 'modules/payroll_exporter/lib/format'
import { Integration } from 'modules/payroll_exporter/lib/format'
import legalEntitiesSettings from 'modules/payroll_exporter/store/collections/legalEntitiesSettings'

import StatusTag from './StatusTag'

interface Props {
  incidence: IncidenceQueryData
  isBookkeeperView: boolean
  toggleEditModal?: (value: boolean) => () => void
}

interface ExportParams {
  type: string
  employee_ids: number[]
  company_id: number | null
  timestamp: number
  bookkeeper_view: boolean
}

const HeadInformation = ({ incidence, isBookkeeperView, toggleEditModal }: Props) => {
  const [downloadDisabled, setDownloadDisabled] = useState<boolean>(false)
  const [openDownloadDescription, toggleDownloadDescription] = useToggle(false)
  const [openIsReopenedTip, toggleIsReopenedTip] = useToggle(false)
  const legalEntityId = incidence.legalEntityId
  const legalEntitySetting = legalEntitiesSettings.findLegalEntity(legalEntityId)

  useAsyncData(
    () =>
      legalEntitiesSettings.fetch({
        data: {
          bookkeeper_view: isBookkeeperView,
        },
      }),
    []
  )

  let integrationId: ExportFormat | null = null
  let integration: Integration | null = null

  if (legalEntitySetting) {
    if (legalEntitySetting.hasFeature('payroll_integrations_a3nom')) {
      integrationId = ExportFormat.A3NOM_EMPLOYEES
      integration = Integration.A3NOM
    }
  }

  function isDownloadable() {
    if (legalEntitySetting) {
      const status = incidence.status
      const type = incidence.name
      const downloadableStatuses = new Set<string>([INCIDENCE_STATUSES.to_do])
      const hasFeatures = legalEntitySetting.hasFeature('payroll_integrations_a3nom')

      return downloadableStatuses.has(status) && type === INCIDENCE_TYPES.hiring && hasFeatures
    }

    return false
  }

  const onDownload = async () => {
    if (!integrationId || incidence.employeeId == null) {
      return
    }

    setDownloadDisabled(true)

    const data: ExportParams = {
      type: integrationId,
      employee_ids: [incidence.employeeId],
      company_id: incidence.legalEntity?.companyId || null,
      /* Used to identify the error export with its export
      for different requests with the same params */
      timestamp: Date.now(),
      bookkeeper_view: true,
    }

    await downloadFile(param(data), data)

    setDownloadDisabled(false)
  }
  const isManualIncidence = incidence?.name === INCIDENCE_TYPES.manual_incidence
  const isManualIncidenceEditable = !isBookkeeperView && isManualIncidence
  const { hasInhousePayroll } = useInhousePayroll()

  return (
    <Box paddingX="s24" paddingBottom="s16" gap="s24">
      <Box justifyContent="spaceBetween" flexDirection="row">
        <Box flexDirection="row" gap="s16" alignItems="center">
          <StatusTag
            text={statusTranslation(incidence.status, isBookkeeperView, hasInhousePayroll)}
            type={getBookkeeperStatusTagColor(incidence.status, hasInhousePayroll)}
          />
          {incidence.isReopened && (
            <Tooltip.ItemList
              size="small"
              open={openIsReopenedTip}
              attachment="bottom center"
              toggle={toggleIsReopenedTip}
            >
              <Tag type={reopenedTagColor()}>
                {i18n.t('bookkeepers.incidence.is_reopened.title')}
              </Tag>
              <Text.Normal size="100">
                {i18n.t('bookkeepers.incidence.is_reopened.description')}
              </Text.Normal>
            </Tooltip.ItemList>
          )}
          {isDownloadable() && isBookkeeperView && integration && (
            <Tooltip.Alt
              attachment="top center"
              targetAttachment="bottom center"
              open={openDownloadDescription}
              toggle={toggleDownloadDescription}
              label={i18n.t('common.download_file_name', { name: integration })}
            >
              <Button
                disabled={downloadDisabled}
                type="secondary"
                onClick={onDownload}
                aria-label="Download A3"
                iconName="download"
              />
            </Tooltip.Alt>
          )}
        </Box>
        {isManualIncidenceEditable && toggleEditModal && (
          <Button type="secondary" onClick={toggleEditModal(true)} iconName="pencil" />
        )}
      </Box>
    </Box>
  )
}

export default observer(HeadInformation)
